import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDropzone } from 'react-dropzone';
import {
    Box,
    FormControlLabel,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Paper,
    Switch,
    Typography,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Checkbox,
    Tooltip,
    IconButton,
    CircularProgress,
    Backdrop,
} from '@mui/material';
import { ViewModule as ViewModuleIcon } from '@mui/icons-material';
import FolderIcon from '@mui/icons-material/Folder';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import Swal from 'sweetalert2';
import { alpha, type Theme } from '@mui/material/styles';

import { SCUIContent } from '../SCUIContent/SCUIContent';
import { SCUIButton } from '../../atoms';
import CopyToClipboardButton from '../../atoms/SCUICopyToClipboardButton/SCUICopyToClipboardButton';
import {
    GetS3List,
    S3FileItem,
    S3FolderItem,
    S3ListResponse,
    MAX_S3_SELECTION_KEYS,
    getFileNameFromKey,
} from '../../../services/query/aiPrompt/S3BrowserQuery.services';
import {
    computeDisplayPath,
    isPreviewableImageKey,
    mergeListItemsByKey,
    openS3BrowserFile,
    trimTrailingSlashes,
} from './SCUIS3Browser.helpers';
import { S3BrowserContextMenu, type CtxMenuState, type CtxTarget } from './S3BrowserContextMenu';
import { S3BrowserDownloadPanel } from './S3BrowserDownloadPanel';
import { S3FileListRow } from './S3FileListRow';
import { useSCUIS3BrowserDownloads } from './useSCUIS3BrowserDownloads';
import { useS3PreviewImageSource } from './useS3PreviewImageSource';
import {
    executeCreateFolder,
    executeDeleteObject,
    executeDeleteSelectedFiles,
    executeMoveObjects,
    executeRenameObject,
    executeUploadFiles,
    resolveMoveKeysForContextTarget,
} from './SCUIS3Browser.operations';

type S3InlinePreviewProps = {
    file: S3FileItem;
};

const S3InlineFilePreview: React.FC<S3InlinePreviewProps> = ({ file }) => {
    const { t } = useTranslation();
    const [fullscreenOpen, setFullscreenOpen] = useState(false);
    const preview = useS3PreviewImageSource(file, { preferThumbnail: true });
    const full = useS3PreviewImageSource(file, {
        enabled: fullscreenOpen,
        preferThumbnail: false,
    });

    const closeFullscreen = () => setFullscreenOpen(false);

    if (!isPreviewableImageKey(file.key)) {
        return (
            <Box sx={{ textAlign: 'center', py: 3, px: 1 }}>
                <InsertDriveFileIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                    {t('aiPrompt.s3Browser.previewNoImage')}
                </Typography>
            </Box>
        );
    }

    if (preview.loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                <CircularProgress size={36} />
            </Box>
        );
    }

    if (preview.error || !preview.src) {
        return (
            <Typography variant="body2" color="error" sx={{ textAlign: 'center', py: 2 }}>
                {t('aiPrompt.s3Browser.previewLoadError')}
            </Typography>
        );
    }

    return (
        <>
            <Box
                component="img"
                src={preview.src}
                alt={file.name}
                onClick={() => setFullscreenOpen(true)}
                sx={{
                    maxWidth: '100%',
                    maxHeight: 360,
                    width: 'auto',
                    height: 'auto',
                    objectFit: 'contain',
                    borderRadius: 1,
                    display: 'block',
                    mx: 'auto',
                    cursor: 'zoom-in',
                }}
            />
            <Dialog
                open={fullscreenOpen}
                onClose={closeFullscreen}
                maxWidth="lg"
                fullWidth
                slotProps={{ backdrop: { sx: { bgcolor: 'rgba(0,0,0,0.85)' } } }}
            >
                <DialogContent
                    sx={{
                        p: 0,
                        position: 'relative',
                        bgcolor: 'black',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: { xs: '60vh', md: '80vh' },
                    }}
                >
                    <IconButton
                        onClick={closeFullscreen}
                        aria-label={t('general.close')}
                        sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            color: 'white',
                            bgcolor: 'rgba(0,0,0,0.5)',
                            zIndex: 1,
                            '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                    {full.loading ? (
                        <Box
                            sx={{
                                position: 'relative',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                            }}
                        >
                            <Box
                                component="img"
                                src={preview.src}
                                alt={file.name}
                                sx={{ maxWidth: '100%', maxHeight: '90vh', objectFit: 'contain', opacity: 0.4 }}
                            />
                            <CircularProgress sx={{ position: 'absolute', color: 'white' }} size={48} />
                        </Box>
                    ) : full.error || !full.src ? (
                        <Typography color="error" sx={{ color: 'white' }}>
                            {t('aiPrompt.s3Browser.previewLoadError')}
                        </Typography>
                    ) : (
                        <Box
                            component="img"
                            src={full.src}
                            alt={file.name}
                            sx={{
                                maxWidth: '100%',
                                maxHeight: '90vh',
                                width: 'auto',
                                height: 'auto',
                                objectFit: 'contain',
                                display: 'block',
                            }}
                        />
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
};
type S3FilePreviewPanelProps = {
    previewFile: S3FileItem | null;
};

const S3FilePreviewPanel: React.FC<S3FilePreviewPanelProps> = ({ previewFile }) => {
    const { t } = useTranslation();

    return (
        <Box sx={{ width: { xs: '100%', md: 380 }, flexShrink: 0 }}>
            <Paper
                variant="outlined"
                sx={(theme) => ({
                    p: 2,
                    minHeight: 280,
                    height: { md: '100%' },
                    maxHeight: { md: '65vh' },
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    borderColor: theme.palette.divider,
                })}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <ImageOutlinedIcon color="primary" fontSize="small" />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {t('aiPrompt.s3Browser.previewTitle')}
                    </Typography>
                </Box>
                {previewFile ? (
                    <>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 1.5 }}>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                    flex: 1,
                                    wordBreak: 'break-all',
                                    minWidth: 0,
                                    userSelect: 'text',
                                    cursor: 'text',
                                }}
                            >
                                {previewFile.name}
                            </Typography>
                            <Tooltip title={t('general.copyToClipboard')}>
                                <Box sx={{ flexShrink: 0, lineHeight: 0 }}>
                                    <CopyToClipboardButton
                                        textToCopy={previewFile.name}
                                        size="small"
                                        sx={{ p: 0.25 }}
                                    />
                                </Box>
                            </Tooltip>
                        </Box>
                        <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
                            <S3InlineFilePreview file={previewFile} />
                        </Box>
                    </>
                ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {t('aiPrompt.s3Browser.previewPlaceholder')}
                    </Typography>
                )}
            </Paper>
        </Box>
    );
};

type ThumbnailCardProps = {
    file: S3FileItem;
    isSelected: boolean;
    isPreviewSelected: boolean;
    fileSelectionEnabled: boolean;
    loading: boolean;
    onToggleSelect: (key: string) => void;
    onContextMenu: (e: React.MouseEvent, target: CtxTarget) => void;
    onClick: (file: S3FileItem) => void;
};

function getThumbnailCardSx(theme: Theme, isPreviewSelected: boolean, isSelected: boolean) {
    const highlighted = isPreviewSelected || isSelected;
    const borderColor = isPreviewSelected
        ? theme.palette.primary.main
        : isSelected
          ? theme.palette.primary.light
          : theme.palette.divider;

    return {
        position: 'relative' as const,
        width: 180,
        borderRadius: 2,
        overflow: 'hidden' as const,
        border: `2px solid ${borderColor}`,
        bgcolor: highlighted ? alpha(theme.palette.primary.main, 0.06) : 'background.paper',
        boxShadow: highlighted
            ? `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`
            : '0 1px 3px rgba(0,0,0,0.08)',
        transition: 'border-color 0.15s, box-shadow 0.15s, transform 0.15s',
        cursor: 'pointer',
        flexShrink: 0,
        '&:hover': {
            borderColor: theme.palette.primary.light,
            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`,
            transform: 'translateY(-2px)',
        },
    };
}

const ThumbnailCard: React.FC<ThumbnailCardProps> = React.memo(
    ({
        file,
        isSelected,
        isPreviewSelected,
        fileSelectionEnabled,
        loading,
        onToggleSelect,
        onContextMenu,
        onClick,
    }) => {
        const containerRef = useRef<HTMLDivElement>(null);
        const [isVisible, setIsVisible] = useState(false);
        const image = useS3PreviewImageSource(file, { enabled: isVisible, preferThumbnail: true });

        useEffect(() => {
            const el = containerRef.current;
            if (!el) return;
            const observer = new IntersectionObserver(
                ([entry]) => {
                    if (entry.isIntersecting) {
                        setIsVisible(true);
                        observer.disconnect();
                    }
                },
                { rootMargin: '200px' }
            );
            observer.observe(el);
            return () => observer.disconnect();
        }, []);

        const isImage = isPreviewableImageKey(file.key);
        const showSpinner = !isVisible || image.loading;
        const showFallback = image.error || !image.src;

        const handleToggleSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
            e.stopPropagation();
            onToggleSelect(file.key);
        };

        return (
            <Box
                ref={containerRef}
                onClick={() => onClick(file)}
                onContextMenu={(e) => onContextMenu(e, { kind: 'file', item: file })}
                sx={(theme) => getThumbnailCardSx(theme, isPreviewSelected, isSelected)}
            >
                {fileSelectionEnabled && (
                    <Checkbox
                        size="small"
                        checked={isSelected}
                        onChange={handleToggleSelect}
                        onClick={(e) => e.stopPropagation()}
                        disabled={loading}
                        sx={{
                            position: 'absolute',
                            top: 2,
                            left: 2,
                            zIndex: 2,
                            bgcolor: 'rgba(255,255,255,0.85)',
                            borderRadius: 1,
                        }}
                    />
                )}

                <Box
                    sx={{
                        width: '100%',
                        height: 140,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: '#f5f5f5',
                        overflow: 'hidden',
                    }}
                >
                    {showSpinner ? (
                        <CircularProgress size={24} />
                    ) : showFallback ? (
                        isImage ? (
                            <ImageOutlinedIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
                        ) : (
                            <InsertDriveFileIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
                        )
                    ) : (
                        <Box
                            component="img"
                            src={image.src!}
                            alt={file.name}
                            loading="lazy"
                            sx={{
                                maxWidth: '100%',
                                maxHeight: '100%',
                                width: 'auto',
                                height: 'auto',
                                objectFit: 'contain',
                            }}
                        />
                    )}
                </Box>

                <Box sx={{ px: 1, py: 0.75, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Typography
                        variant="caption"
                        sx={{
                            fontWeight: 600,
                            display: 'block',
                            lineHeight: 1.3,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {file.name}
                    </Typography>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                            display: 'block',
                            lineHeight: 1.2,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontSize: '0.65rem',
                        }}
                    >
                        {file.key}
                    </Typography>
                </Box>
            </Box>
        );
    }
);

const toolbarIconButtonSx = (theme: Theme) => ({
    width: 52,
    height: 52,
    borderRadius: 2,
    transition: 'background-color 0.2s ease, transform 0.15s ease',
    '&:hover:not(:disabled)': {
        bgcolor: alpha(theme.palette.primary.main, 0.12),
        transform: 'translateY(-1px)',
    },
    '& .MuiSvgIcon-root': { fontSize: 28 },
});

const newFolderIconButtonSx = (theme: Theme) => ({
    ...toolbarIconButtonSx(theme),
    bgcolor: alpha(theme.palette.primary.main, 0.14),
    color: theme.palette.primary.main,
    '&:hover:not(:disabled)': {
        bgcolor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        transform: 'translateY(-1px)',
    },
});

export interface SCUIS3BrowserProps {
    basePrefix?: string;
    onFileSelect?: (file: S3FileItem) => void;
    readOnly?: boolean;
    acceptedFileTypes?: string;
    disableUpload?: boolean;
    disableCreateFolder?: boolean;
    disableDelete?: boolean;
    disableRename?: boolean;
    disableMove?: boolean;
    disableDownload?: boolean;
    /** Varsayılan true. Yalnızca tam S3 Browser sayfasında açık tutulmalı. */
    showPreviewPanel?: boolean;
    /** true iken dosya checkbox’ları, sayfadaki tümünü seç ve seçime bağlı araç çubuğu aksiyonları gizlenir. */
    disableFileSelection?: boolean;
    onPrefixChange?: (prefix: string) => void;
    onUploadComplete?: () => void;
    /** Indirme kuyrugu basladiginda/bittiginde true/false. */
    onDownloadingChange?: (downloading: boolean) => void;
    customUploadHandler?: (files: File[], prefix: string) => Promise<void>;
    hideContentWrapper?: boolean;
}

export const SCUIS3Browser: React.FC<SCUIS3BrowserProps> = (props) => {
    const { t } = useTranslation();
    const showPreviewPanel = props.showPreviewPanel !== false;
    const fileSelectionEnabled = props.disableFileSelection !== true;
    const effectiveBasePrefix = useMemo(() => {
        if (props.basePrefix) {
            return props.basePrefix;
        }
        const env = trimTrailingSlashes(String(import.meta.env.VITE_ENVIRONMENT || 'DEV').trim());
        return env ? `${env}/` : '';
    }, [props.basePrefix]);

    const [prefix, setPrefix] = useState(effectiveBasePrefix);
    const [folders, setFolders] = useState<S3FolderItem[]>([]);
    const [files, setFiles] = useState<S3FileItem[]>([]);
    const [isTruncated, setIsTruncated] = useState(false);
    const [nextToken, setNextToken] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [folderDialogOpen, setFolderDialogOpen] = useState(false);
    const [newFolderName, setNewFolderName] = useState('');
    const [creatingFolder, setCreatingFolder] = useState(false);
    const [ctxMenu, setCtxMenu] = useState<CtxMenuState | null>(null);
    const [renameOpen, setRenameOpen] = useState(false);
    const [renameValue, setRenameValue] = useState('');
    const [renameTarget, setRenameTarget] = useState<CtxTarget | null>(null);
    const [renaming, setRenaming] = useState(false);
    const [moveOpen, setMoveOpen] = useState(false);
    const [moveKeys, setMoveKeys] = useState<string[]>([]);
    const [moveDestPrefix, setMoveDestPrefix] = useState('');
    const [moving, setMoving] = useState(false);
    const [selectedKeys, setSelectedKeys] = useState<Set<string>>(() => new Set());
    const [previewKey, setPreviewKey] = useState<string | null>(null);
    const [thumbnailView, setThumbnailView] = useState(false);
    const THUMBNAIL_PAGE_SIZE = 5;
    const [thumbnailVisibleCount, setThumbnailVisibleCount] = useState(THUMBNAIL_PAGE_SIZE);

    const {
        downloading,
        downloadItems,
        downloadStatusExpanded,
        setDownloadStatusExpanded,
        downloadElapsedMs,
        lastDownloadDurationMs,
        downloadCompletedCount,
        downloadFailedCount,
        showDownloadPanel,
        retryFailedDownloads,
        downloadFile,
        downloadFolder,
        downloadSelected,
        clearDownloadPanel,
    } = useSCUIS3BrowserDownloads({
        prefix,
        effectiveBasePrefix,
        onDownloadingChange: props.onDownloadingChange,
    });

    const hasFiles = files.length > 0;
    const effectiveThumbnailView = thumbnailView && hasFiles;

    useEffect(() => {
        setThumbnailVisibleCount(THUMBNAIL_PAGE_SIZE);
    }, [prefix]);

    useEffect(() => {
        if (!loading && !hasFiles && thumbnailView) {
            setThumbnailView(false);
        }
    }, [hasFiles, loading, thumbnailView]);

    useEffect(() => {
        if (props.onPrefixChange) {
            props.onPrefixChange(prefix);
        }
    }, [prefix, props.onPrefixChange]);

    const previewFile = useMemo(
        () => (previewKey ? (files.find((f) => f.key === previewKey) ?? null) : null),
        [previewKey, files]
    );

    useEffect(() => {
        if (previewKey && !files.some((f) => f.key === previewKey)) {
            setPreviewKey(null);
        }
    }, [files, previewKey]);

    const applyListResponse = useCallback((data: S3ListResponse, append: boolean) => {
        setFolders((prev) => mergeListItemsByKey(prev, data.folders, append));
        setFiles((prev) => mergeListItemsByKey(prev, data.files, append));
        setIsTruncated(!!data.isTruncated);
        setNextToken(data.nextContinuationToken || null);
        setPrefix(data.prefix ?? '');
    }, []);

    const load = useCallback(
        async (targetPrefix: string, continuationToken?: string | null, append = false) => {
            if (!append) {
                setSelectedKeys(new Set());
                setPreviewKey(null);
                setFolders([]);
                setFiles([]);
            }
            try {
                setLoading(true);
                setError(null);
                const data = await GetS3List({
                    prefix: targetPrefix,
                    continuationToken: continuationToken || undefined,
                });
                applyListResponse(data, append);
            } catch {
                setError(t('aiPrompt.s3Browser.loadError'));
                if (!append) {
                    setFolders([]);
                    setFiles([]);
                    setIsTruncated(false);
                    setNextToken(null);
                }
            } finally {
                setLoading(false);
            }
        },
        [applyListResponse, t]
    );

    useEffect(() => {
        setPrefix(effectiveBasePrefix);
        load(effectiveBasePrefix, null, false);
    }, [load, effectiveBasePrefix]);

    const goUp = () => {
        const p = prefix.replace(/\/$/, '');
        if (!p || p === effectiveBasePrefix.replace(/\/$/, '')) return; // Do not go above basePrefix
        const idx = p.lastIndexOf('/');
        const parent = idx <= 0 ? '' : `${p.slice(0, idx + 1)}`;
        load(parent, null, false);
    };

    const isAtRoot = prefix === effectiveBasePrefix;

    const openFolder = (key: string) => {
        load(key, null, false);
    };

    const openFile = (file: S3FileItem) => {
        openS3BrowserFile({
            file,
            showPreviewPanel,
            onFileSelect: props.onFileSelect,
            downloadErrorTitle: t('aiPrompt.s3Browser.downloadError'),
        }).catch(() => undefined);
    };

    const handleFilePreviewClick = (file: S3FileItem) => {
        if (showPreviewPanel && !loading) {
            setPreviewKey(file.key);
            return;
        }
        openFile(file);
    };

    const loadMore = () => {
        if (nextToken) {
            load(prefix, nextToken, true);
        }
    };

    const openFolderDialog = () => {
        setNewFolderName('');
        setFolderDialogOpen(true);
    };

    const closeFolderDialog = () => {
        if (!creatingFolder) {
            setFolderDialogOpen(false);
            setNewFolderName('');
        }
    };

    const submitNewFolder = async () => {
        await executeCreateFolder({
            prefix,
            newFolderName,
            t,
            setCreatingFolder,
            resetFolderDialog: () => {
                setFolderDialogOpen(false);
                setNewFolderName('');
            },
            reload: async (targetPrefix) => load(targetPrefix, null, false),
        });
    };

    const closeCtxMenu = () => setCtxMenu(null);

    const openContextMenu = (e: React.MouseEvent, target: CtxTarget) => {
        e.preventDefault();
        e.stopPropagation();
        setCtxMenu({ mouseX: e.clientX, mouseY: e.clientY, target });
    };

    const getRenameSegment = (target: CtxTarget) => getFileNameFromKey(target.item.key);

    const openRenameDialog = (target: CtxTarget) => {
        setRenameTarget(target);
        setRenameValue(getRenameSegment(target));
        setRenameOpen(true);
        closeCtxMenu();
    };

    const closeRenameDialog = () => {
        if (!renaming) {
            setRenameOpen(false);
            setRenameTarget(null);
            setRenameValue('');
        }
    };

    const openMoveDialog = (target: CtxTarget) => {
        setMoveKeys(resolveMoveKeysForContextTarget(target, fileSelectionEnabled, selectedKeys));
        setMoveDestPrefix('');
        setMoveOpen(true);
        closeCtxMenu();
    };

    const openMoveSelectedDialog = () => {
        if (!fileSelectionEnabled || props.readOnly || props.disableMove || selectedKeys.size === 0) return;
        setMoveKeys(Array.from(selectedKeys));
        setMoveDestPrefix('');
        setMoveOpen(true);
    };

    const closeMoveDialog = () => {
        if (!moving) {
            setMoveOpen(false);
            setMoveKeys([]);
            setMoveDestPrefix('');
        }
    };

    const submitMove = async () => {
        await executeMoveObjects({
            moveKeys,
            moveDestPrefix,
            prefix,
            t,
            setMoving,
            setSelectedKeys,
            resetMoveDialog: () => {
                setMoveOpen(false);
                setMoveKeys([]);
                setMoveDestPrefix('');
            },
            reload: async (targetPrefix) => load(targetPrefix, null, false),
        });
    };

    const submitRename = async () => {
        await executeRenameObject({
            renameTarget,
            renameValue,
            prefix,
            t,
            setRenaming,
            resetRenameDialog: () => {
                setRenameOpen(false);
                setRenameTarget(null);
                setRenameValue('');
            },
            reload: async (targetPrefix) => load(targetPrefix, null, false),
        });
    };

    const submitDelete = async (target: CtxTarget) => {
        await executeDeleteObject({
            target,
            prefix,
            t,
            setLoading,
            reload: async (targetPrefix) => load(targetPrefix, null, false),
        });
    };

    const handleUpload = async (acceptedFiles: File[]) => {
        await executeUploadFiles({
            acceptedFiles,
            prefix,
            readOnly: props.readOnly,
            disableUpload: props.disableUpload,
            customUploadHandler: props.customUploadHandler,
            t,
            setUploading,
            reload: async (targetPrefix) => load(targetPrefix, null, false),
            onUploadComplete: props.onUploadComplete,
        });
    };

    const {
        getRootProps,
        getInputProps,
        isDragActive,
        open: openFileDialog,
    } = useDropzone({
        onDrop: handleUpload,
        noClick: true,
        noKeyboard: true,
        disabled: props.readOnly || props.disableUpload || uploading,
        accept: props.acceptedFileTypes || undefined,
    });

    const toggleFileSelected = (fileKey: string) => {
        setSelectedKeys((prev) => {
            const next = new Set(prev);
            if (next.has(fileKey)) {
                next.delete(fileKey);
                return next;
            }
            if (next.size >= MAX_S3_SELECTION_KEYS) {
                void Swal.fire({
                    icon: 'warning',
                    title: t('aiPrompt.s3Browser.selectionLimitReached', { max: MAX_S3_SELECTION_KEYS }),
                });
                return prev;
            }
            next.add(fileKey);
            return next;
        });
    };

    const selectAllFilesOnPage = () => {
        setSelectedKeys((prev) => {
            const next = new Set(prev);
            for (const f of files) {
                if (next.size >= MAX_S3_SELECTION_KEYS) {
                    void Swal.fire({
                        icon: 'warning',
                        title: t('aiPrompt.s3Browser.selectionLimitReached', { max: MAX_S3_SELECTION_KEYS }),
                    });
                    break;
                }
                next.add(f.key);
            }
            return next;
        });
    };

    const clearFileSelection = () => setSelectedKeys(new Set());

    const deleteSelectedFiles = async () => {
        await executeDeleteSelectedFiles({
            selectedKeys,
            prefix,
            t,
            setLoading,
            clearSelection: () => setSelectedKeys(new Set()),
            reload: async (targetPrefix) => load(targetPrefix, null, false),
        });
    };

    const displayPath = useMemo(() => {
        if (!prefix) return t('aiPrompt.s3Browser.root');
        return computeDisplayPath(prefix, String(import.meta.env.VITE_ENVIRONMENT ?? '').trim());
    }, [prefix, t]);
    const embedded = props.hideContentWrapper === true;

    const listPaperSx = embedded
        ? {
              flex: 1,
              minHeight: 0,
              maxHeight: '65vh',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
          }
        : {
              minHeight: 300,
              maxHeight: '65vh',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
          };

    const innerContent = (
        <>
            <Backdrop
                sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1, backdropFilter: 'blur(3px)' }}
                open={uploading || moving}
            >
                <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    gap={2}
                    sx={{ width: '80%', maxWidth: 450 }}
                >
                    <CircularProgress color="inherit" size={60} />
                    <Typography variant="h6" align="center" sx={{ fontWeight: 500 }}>
                        {uploading ? t('aiPrompt.s3Browser.uploading') : t('aiPrompt.s3Browser.moving')}
                    </Typography>
                </Box>
            </Backdrop>

            {showDownloadPanel && (
                <S3BrowserDownloadPanel
                    downloadItems={downloadItems}
                    downloadStatusExpanded={downloadStatusExpanded}
                    onExpandedChange={setDownloadStatusExpanded}
                    downloadCompletedCount={downloadCompletedCount}
                    downloadFailedCount={downloadFailedCount}
                    downloading={downloading}
                    downloadElapsedMs={downloadElapsedMs}
                    lastDownloadDurationMs={lastDownloadDurationMs}
                    onClear={clearDownloadPanel}
                    onRetryFailed={() => {
                        retryFailedDownloads().catch(() => undefined);
                    }}
                />
            )}

            <Box
                sx={(theme) => ({
                    mb: 2,
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1,
                    alignItems: 'center',
                    p: 1.25,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.06),
                    border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
                    boxShadow: `0 1px 3px ${alpha(theme.palette.common.black, 0.06)}`,
                    ...(embedded && { flexShrink: 0 }),
                })}
            >
                <Tooltip title={t('aiPrompt.s3Browser.parentFolder')} enterDelay={400}>
                    <span>
                        <IconButton
                            onClick={goUp}
                            disabled={loading || downloading || !prefix || isAtRoot}
                            color="default"
                            aria-label={t('aiPrompt.s3Browser.parentFolder')}
                            sx={(theme) => toolbarIconButtonSx(theme)}
                        >
                            <ArrowBackIcon />
                        </IconButton>
                    </span>
                </Tooltip>
                {!props.readOnly && !props.disableCreateFolder && (
                    <Tooltip title={t('aiPrompt.s3Browser.newFolder')} enterDelay={400}>
                        <span>
                            <IconButton
                                onClick={openFolderDialog}
                                disabled={loading || uploading}
                                aria-label={t('aiPrompt.s3Browser.newFolder')}
                                sx={(theme) => newFolderIconButtonSx(theme)}
                            >
                                <CreateNewFolderIcon />
                            </IconButton>
                        </span>
                    </Tooltip>
                )}
                {!props.readOnly && !props.disableUpload && (
                    <Tooltip title={t('aiPrompt.s3Browser.upload')} enterDelay={400}>
                        <span>
                            <IconButton
                                onClick={openFileDialog}
                                disabled={loading || uploading}
                                aria-label={t('aiPrompt.s3Browser.upload')}
                                sx={(theme) => newFolderIconButtonSx(theme)}
                            >
                                <CloudUploadIcon />
                            </IconButton>
                        </span>
                    </Tooltip>
                )}
                {!props.readOnly && !props.disableDelete && fileSelectionEnabled && (
                    <Tooltip title={t('aiPrompt.s3Browser.deleteSelected')} enterDelay={400}>
                        <span>
                            <IconButton
                                onClick={() => void deleteSelectedFiles()}
                                disabled={loading || selectedKeys.size === 0}
                                color="error"
                                aria-label={t('aiPrompt.s3Browser.deleteSelected')}
                                sx={(theme) => toolbarIconButtonSx(theme)}
                            >
                                <DeleteIcon />
                            </IconButton>
                        </span>
                    </Tooltip>
                )}
                {!props.readOnly && !props.disableMove && fileSelectionEnabled && (
                    <Tooltip title={t('aiPrompt.s3Browser.moveSelected')} enterDelay={400}>
                        <span>
                            <IconButton
                                onClick={openMoveSelectedDialog}
                                disabled={loading || moving || selectedKeys.size === 0}
                                color="default"
                                aria-label={t('aiPrompt.s3Browser.moveSelected')}
                                sx={(theme) => toolbarIconButtonSx(theme)}
                            >
                                <DriveFileMoveIcon />
                            </IconButton>
                        </span>
                    </Tooltip>
                )}
                {!props.disableDownload && fileSelectionEnabled && (
                    <Tooltip title={t('aiPrompt.s3Browser.downloadSelected')} enterDelay={400}>
                        <span>
                            <IconButton
                                onClick={() => {
                                    downloadSelected(selectedKeys, files).catch(() => undefined);
                                }}
                                disabled={loading || downloading || selectedKeys.size === 0}
                                color="default"
                                aria-label={t('aiPrompt.s3Browser.downloadSelected')}
                                sx={(theme) => toolbarIconButtonSx(theme)}
                            >
                                <FileDownloadIcon />
                            </IconButton>
                        </span>
                    </Tooltip>
                )}
                <Box
                    sx={(theme) => ({
                        ml: 'auto',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        pl: 1,
                        borderLeft: `1px solid ${theme.palette.divider}`,
                    })}
                >
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                        {displayPath || '/'}
                    </Typography>
                    {hasFiles && (
                        <FormControlLabel
                            sx={{
                                ml: 1.5,
                                mr: 0,
                                borderLeft: (theme) => `1px solid ${theme.palette.divider}`,
                                pl: 1.5,
                            }}
                            control={
                                <Switch
                                    size="small"
                                    checked={thumbnailView}
                                    onChange={(e) => setThumbnailView(e.target.checked)}
                                />
                            }
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <ViewModuleIcon fontSize="small" color={thumbnailView ? 'primary' : 'action'} />
                                    <Typography variant="caption" sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
                                        {t('aiPrompt.s3Browser.thumbnailView')}
                                    </Typography>
                                </Box>
                            }
                            labelPlacement="start"
                        />
                    )}
                </Box>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {isTruncated && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    {t('aiPrompt.s3Browser.truncatedWarning')}
                </Alert>
            )}

            {files.length > 0 && fileSelectionEnabled && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, px: 1 }}>
                    <Checkbox
                        checked={selectedKeys.size === files.length && files.length > 0}
                        indeterminate={selectedKeys.size > 0 && selectedKeys.size < files.length}
                        onChange={(e) => {
                            if (e.target.checked) {
                                selectAllFilesOnPage();
                            } else {
                                clearFileSelection();
                            }
                        }}
                        disabled={loading}
                        color="primary"
                    />
                    <Typography
                        variant="body2"
                        sx={{ fontWeight: 500, cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => {
                            if (loading) return;
                            if (selectedKeys.size === files.length && files.length > 0) {
                                clearFileSelection();
                            } else {
                                selectAllFilesOnPage();
                            }
                        }}
                    >
                        {t('aiPrompt.s3Browser.selectAllOnPage')}
                    </Typography>
                </Box>
            )}

            <Box
                {...getRootProps()}
                sx={{
                    position: 'relative',
                    ...(embedded && {
                        flex: 1,
                        minHeight: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                    }),
                }}
            >
                <input {...getInputProps()} />
                {isDragActive && !props.readOnly && !props.disableUpload && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            bgcolor: 'rgba(0,0,0,0.3)',
                            zIndex: 10,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#fff',
                            borderRadius: 1,
                            pointerEvents: 'none',
                        }}
                    >
                        <Typography variant="h5" fontWeight="bold">
                            {t('aiPrompt.s3Browser.dropFilesHere')}
                        </Typography>
                    </Box>
                )}
                {effectiveThumbnailView ? (
                    /* ========== THUMBNAIL GRID VIEW ========== */
                    <Box
                        sx={{
                            display: 'flex',
                            gap: 2,
                            flexDirection: { xs: 'column', md: 'row' },
                            alignItems: 'stretch',
                            ...(embedded && { flex: 1, minHeight: 0, overflow: 'hidden' }),
                        }}
                    >
                        <Box
                            sx={{
                                flex: 1,
                                minWidth: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                ...(embedded && { minHeight: 0, overflow: 'hidden' }),
                            }}
                        >
                            {folders.length > 0 && (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                                    {folders.map((f) => (
                                        <Box
                                            key={f.key}
                                            onClick={() => openFolder(f.key)}
                                            onContextMenu={(e) => openContextMenu(e, { kind: 'folder', item: f })}
                                            sx={(theme) => ({
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.75,
                                                px: 1.5,
                                                py: 0.75,
                                                borderRadius: 1.5,
                                                border: `1px solid ${theme.palette.divider}`,
                                                bgcolor: alpha(theme.palette.primary.main, 0.04),
                                                cursor: 'pointer',
                                                transition: 'background-color 0.15s, border-color 0.15s',
                                                '&:hover': {
                                                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                                                    borderColor: theme.palette.primary.light,
                                                },
                                                opacity: loading ? 0.55 : 1,
                                            })}
                                        >
                                            <FolderIcon color="primary" fontSize="small" />
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                {f.name}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                            )}

                            <Paper
                                variant="outlined"
                                sx={{
                                    flex: 1,
                                    minHeight: 0,
                                    maxHeight: '65vh',
                                    overflow: 'auto',
                                    p: 2,
                                }}
                            >
                                {files.length > 0 ? (
                                    <>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                                gap: 1.5,
                                                alignContent: 'flex-start',
                                            }}
                                        >
                                            {files.slice(0, thumbnailVisibleCount).map((file) => (
                                                <ThumbnailCard
                                                    key={file.key}
                                                    file={file}
                                                    isSelected={selectedKeys.has(file.key)}
                                                    isPreviewSelected={showPreviewPanel && previewKey === file.key}
                                                    fileSelectionEnabled={fileSelectionEnabled}
                                                    loading={loading}
                                                    onToggleSelect={toggleFileSelected}
                                                    onContextMenu={openContextMenu}
                                                    onClick={handleFilePreviewClick}
                                                />
                                            ))}
                                        </Box>
                                        {thumbnailVisibleCount < files.length && (
                                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                                <SCUIButton
                                                    type="secondary"
                                                    sizeType="default"
                                                    size="m"
                                                    text="Daha fazla yükle"
                                                    onClick={() =>
                                                        setThumbnailVisibleCount((prev) => prev + THUMBNAIL_PAGE_SIZE)
                                                    }
                                                />
                                            </Box>
                                        )}
                                    </>
                                ) : !loading && folders.length === 0 && !error ? (
                                    <Box sx={{ p: 3 }}>
                                        <Typography color="text.secondary">{t('aiPrompt.s3Browser.empty')}</Typography>
                                    </Box>
                                ) : null}
                            </Paper>

                            {!error && (
                                <Box
                                    sx={{
                                        mt: 1,
                                        px: 0.5,
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        alignItems: 'center',
                                        gap: 2,
                                        rowGap: 0.5,
                                        justifyContent: 'space-between',
                                    }}
                                >
                                    <Typography variant="body2" color="text.secondary" component="div">
                                        {t('aiPrompt.s3Browser.listShown', {
                                            folderCount: folders.length,
                                            fileCount: files.length,
                                        })}
                                    </Typography>
                                    {fileSelectionEnabled && selectedKeys.size > 0 && (
                                        <Typography
                                            variant="body2"
                                            color="primary"
                                            sx={{ fontWeight: 600 }}
                                            component="div"
                                        >
                                            {t('aiPrompt.s3Browser.selectedCount', {
                                                count: selectedKeys.size,
                                                max: MAX_S3_SELECTION_KEYS,
                                            })}
                                        </Typography>
                                    )}
                                </Box>
                            )}
                        </Box>
                        {files.length > 0 && showPreviewPanel && <S3FilePreviewPanel previewFile={previewFile} />}
                    </Box>
                ) : (
                    /* ========== LIST VIEW (original) ========== */
                    <Box
                        sx={{
                            display: 'flex',
                            gap: 2,
                            flexDirection: { xs: 'column', md: 'row' },
                            alignItems: 'stretch',
                            ...(embedded && { flex: 1, minHeight: 0, overflow: 'hidden' }),
                        }}
                    >
                        <Box
                            sx={{
                                flex: 1,
                                minWidth: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                ...(embedded && { minHeight: 0, overflow: 'hidden' }),
                            }}
                        >
                            <Paper
                                variant="outlined"
                                onContextMenu={(e: React.MouseEvent) => e.preventDefault()}
                                sx={listPaperSx as any}
                            >
                                <List dense disablePadding sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                                    {folders.map((f) => (
                                        <ListItemButton
                                            key={f.key}
                                            onClick={() => openFolder(f.key)}
                                            onContextMenu={(e) => openContextMenu(e, { kind: 'folder', item: f })}
                                            disabled={loading}
                                        >
                                            <ListItemIcon>
                                                <FolderIcon color="primary" />
                                            </ListItemIcon>
                                            <ListItemText primary={f.name} secondary={f.key} />
                                        </ListItemButton>
                                    ))}
                                    {files.map((file) => (
                                        <S3FileListRow
                                            key={file.key}
                                            file={file}
                                            isSelected={selectedKeys.has(file.key)}
                                            isPreviewRow={showPreviewPanel && previewKey === file.key}
                                            rowClickablePreview={showPreviewPanel && !loading}
                                            fileSelectionEnabled={fileSelectionEnabled}
                                            loading={loading}
                                            showPreviewPanel={showPreviewPanel}
                                            onPreviewClick={handleFilePreviewClick}
                                            onToggleSelect={toggleFileSelected}
                                            onContextMenu={openContextMenu}
                                        />
                                    ))}
                                    {!loading && folders.length === 0 && files.length === 0 && !error && (
                                        <Box sx={{ p: 3 }}>
                                            <Typography color="text.secondary">
                                                {t('aiPrompt.s3Browser.empty')}
                                            </Typography>
                                        </Box>
                                    )}
                                </List>
                            </Paper>
                            {!error && (
                                <Box
                                    sx={{
                                        mt: 1,
                                        px: 0.5,
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        alignItems: 'center',
                                        gap: 2,
                                        rowGap: 0.5,
                                        justifyContent: 'space-between',
                                    }}
                                >
                                    <Typography variant="body2" color="text.secondary" component="div">
                                        {t('aiPrompt.s3Browser.listShown', {
                                            folderCount: folders.length,
                                            fileCount: files.length,
                                        })}
                                    </Typography>
                                    {fileSelectionEnabled && selectedKeys.size > 0 && (
                                        <Typography
                                            variant="body2"
                                            color="primary"
                                            sx={{ fontWeight: 600 }}
                                            component="div"
                                        >
                                            {t('aiPrompt.s3Browser.selectedCount', {
                                                count: selectedKeys.size,
                                                max: MAX_S3_SELECTION_KEYS,
                                            })}
                                        </Typography>
                                    )}
                                </Box>
                            )}
                        </Box>
                        {files.length > 0 && showPreviewPanel && <S3FilePreviewPanel previewFile={previewFile} />}
                    </Box>
                )}
            </Box>

            {nextToken && (
                <Box sx={{ mt: 2 }}>
                    <SCUIButton
                        type="secondary"
                        sizeType="default"
                        size="m"
                        text={t('aiPrompt.s3Browser.loadMore')}
                        onClick={loadMore}
                        disabled={loading}
                    />
                </Box>
            )}

            {loading && (
                <Typography variant="body2" sx={{ mt: 2 }} color="text.secondary">
                    {t('aiPrompt.s3Browser.loading')}
                </Typography>
            )}

            <S3BrowserContextMenu
                ctxMenu={ctxMenu}
                onClose={closeCtxMenu}
                readOnly={props.readOnly}
                disableRename={props.disableRename}
                disableMove={props.disableMove}
                disableDownload={props.disableDownload}
                disableDelete={props.disableDelete}
                showPreviewPanel={showPreviewPanel}
                downloading={downloading}
                onOpenFolder={openFolder}
                onOpenFile={openFile}
                onRename={openRenameDialog}
                onMove={openMoveDialog}
                onDownloadFolder={(folderKey) => {
                    downloadFolder(folderKey, closeCtxMenu).catch(() => undefined);
                }}
                onDownloadFile={(file) => {
                    downloadFile(file, closeCtxMenu).catch(() => undefined);
                }}
                onDelete={(target) => {
                    submitDelete(target).catch(() => undefined);
                }}
            />

            <Dialog open={folderDialogOpen} onClose={closeFolderDialog} maxWidth="sm" fullWidth>
                <DialogTitle>{t('aiPrompt.s3Browser.newFolderTitle')}</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label={t('aiPrompt.s3Browser.folderName')}
                        placeholder={t('aiPrompt.s3Browser.folderNamePlaceholder')}
                        fullWidth
                        variant="outlined"
                        value={newFolderName}
                        onChange={(e) => setNewFolderName(e.target.value)}
                        disabled={creatingFolder}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                void submitNewFolder();
                            }
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <SCUIButton
                        type="ghost"
                        sizeType="default"
                        size="m"
                        text={t('aiPrompt.s3Browser.cancel')}
                        onClick={closeFolderDialog}
                        disabled={creatingFolder}
                    />
                    <SCUIButton
                        type="primary"
                        sizeType="default"
                        size="m"
                        text={t('aiPrompt.s3Browser.createFolder')}
                        onClick={() => void submitNewFolder()}
                        disabled={creatingFolder}
                    />
                </DialogActions>
            </Dialog>

            <Dialog open={moveOpen} onClose={closeMoveDialog} maxWidth="sm" fullWidth>
                <DialogTitle>{t('aiPrompt.s3Browser.moveTitle')}</DialogTitle>
                <DialogContent>
                    {moveKeys.length === 1 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, wordBreak: 'break-all' }}>
                            {moveKeys[0]}
                        </Typography>
                    ) : moveKeys.length > 1 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {t('aiPrompt.s3Browser.moveMultipleSummary', { count: moveKeys.length })}
                        </Typography>
                    ) : null}
                    <TextField
                        autoFocus
                        margin="dense"
                        label={t('aiPrompt.s3Browser.moveDestinationPrefix')}
                        placeholder={t('aiPrompt.s3Browser.moveDestinationPlaceholder')}
                        helperText={t('aiPrompt.s3Browser.moveDestinationHelp')}
                        fullWidth
                        variant="outlined"
                        value={moveDestPrefix}
                        onChange={(e) => setMoveDestPrefix(e.target.value)}
                        disabled={moving}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                void submitMove();
                            }
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <SCUIButton
                        type="ghost"
                        sizeType="default"
                        size="m"
                        text={t('aiPrompt.s3Browser.cancel')}
                        onClick={closeMoveDialog}
                        disabled={moving}
                    />
                    <SCUIButton
                        type="primary"
                        sizeType="default"
                        size="m"
                        text={t('aiPrompt.s3Browser.moveSave')}
                        onClick={() => void submitMove()}
                        disabled={moving}
                    />
                </DialogActions>
            </Dialog>

            <Dialog open={renameOpen} onClose={closeRenameDialog} maxWidth="sm" fullWidth>
                <DialogTitle>{t('aiPrompt.s3Browser.renameTitle')}</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label={t('aiPrompt.s3Browser.newName')}
                        fullWidth
                        variant="outlined"
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        disabled={renaming}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                void submitRename();
                            }
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <SCUIButton
                        type="ghost"
                        sizeType="default"
                        size="m"
                        text={t('aiPrompt.s3Browser.cancel')}
                        onClick={closeRenameDialog}
                        disabled={renaming}
                    />
                    <SCUIButton
                        type="primary"
                        sizeType="default"
                        size="m"
                        text={t('aiPrompt.s3Browser.renameSave')}
                        onClick={() => void submitRename()}
                        disabled={renaming}
                    />
                </DialogActions>
            </Dialog>
        </>
    );

    if (props.hideContentWrapper) {
        return (
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
                {innerContent}
            </Box>
        );
    }

    return <SCUIContent headerName={t('aiPrompt.s3Browser.title')}>{innerContent}</SCUIContent>;
};

export default SCUIS3Browser;
